import React, { useEffect, useState } from "react";
import { ConfirmButton } from "../../components/ConfirmButton";
import { notification } from "../../helpers/notification";
import { addEvent, deleteEvent, getEvents, updateEvent } from "../../services/eventServices";
import moment from "moment/moment";
import { getEventPlaces } from "../../services/eventPlaceServices";
import { LinkRoute } from "../../components/LinkRoute";
import { onlyOfficeUser } from '../../helpers/utils';

export const EventList = () => {
    const [eventList, setEventList] = useState([]);
    const [eventPlaceList, setEventPlaceList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editUniqueKey, setEditUniqueKey] = useState("");
    const [listStatus, setListStatus] = useState(1);

    const [formFields, setFormFields] = useState({
        name: "",
        totalPersonnelCount: 1,
        chiefSalary: 0,
        oggSalary: 0,
        placeUniqueKey: "",
        startDate: "",
        notes: "",
    });

    const [formErrors, setFormErrors] = useState({});

    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setFormErrors({});
    };

    const getData = () => {
        getEvents(listStatus).then((result) => {
            getEventPlaces().then((placeResult) => {
                setEventPlaceList(placeResult.items);
                setEventList(result.items);
            }).catch(() => {});
        }).catch(() => {});
    };

    useEffect(() => {
        onlyOfficeUser();
        getData();
    }, [listStatus]);

    const onCloseConfirm = (uniqueKey) => {
        updateEvent({
            uniqueKey: uniqueKey,
            isClosed: true
        }).then(() => {
            notification.success({
                message: "Kayıt başarıyla güncellendi.",
            });
            getData();
        }).catch(() => {
            notification.error({
                message: "Bir hata oluştu tekrar deneyiniz",
            });
        });
    };

    const showEditItem = (uniqueKey) => {
        const event = eventList.find(f => f.uniqueKey === uniqueKey);
        setFormFields({
            name: event.name,
            totalPersonnelCount: event.totalPersonnelCount,
            chiefSalary: event.chiefSalary,
            oggSalary: event.oggSalary,
            placeUniqueKey: event.placeUniqueKey,
            startDate: event.startDate ? moment(event.startDate).format("YYYY-MM-DD") : "",
            notes: event.notes || "",
        });
        setEditUniqueKey(uniqueKey);
        setOpen(true);
    };

    const onDeleteConfirm = (uniqueKey) => {
        deleteEvent(uniqueKey).then(() => {
            notification.success({
                message: "Kayıt başarıyla silindi.",
            });
            getData();
        }).catch(() => {
            notification.error({
                message: "Bir hata oluştu tekrar deneyiniz",
            });
        });
    };

    const resetForm = () => {
        setFormFields({
            name: "",
            totalPersonnelCount: 1,
            chiefSalary: 0,
            oggSalary: 0,
            placeUniqueKey: "",
            startDate: "",
            notes: "",
        });
        setEditUniqueKey("");
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};
        if (!formFields.name) errors.name = "Etkinlik Adı zorunludur";
        if (!formFields.totalPersonnelCount) errors.totalPersonnelCount = "Personel sayısı zorunludur";
        if (!formFields.chiefSalary && formFields.chiefSalary !== 0) errors.chiefSalary = "Amir ücreti zorunludur";
        if (!formFields.oggSalary && formFields.oggSalary !== 0) errors.oggSalary = "Özel güvenlik ücreti zorunludur";
        if (!formFields.placeUniqueKey) errors.placeUniqueKey = "Mekan seçiniz";
        if (!formFields.startDate) errors.startDate = "Etkinlik Tarihi zorunludur";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const saveForm = () => {
        if (!validateForm()) return;
        const payload = {
            name: formFields.name,
            totalPersonnelCount: Number(formFields.totalPersonnelCount),
            chiefSalary: Number(formFields.chiefSalary),
            oggSalary: Number(formFields.oggSalary),
            placeUniqueKey: formFields.placeUniqueKey,
            startDate: formFields.startDate ? formFields.startDate + " 13:00:00" : null,
            notes: formFields.notes || "",
        };
        if (editUniqueKey) {
            payload.uniqueKey = editUniqueKey;
            updateEvent(payload).then(() => {
                notification.success({
                    message: "Kayıt başarıyla güncellendi.",
                });
                resetForm();
                setOpen(false);
                getData();
            }).catch(() => {
                notification.error({
                    message: "Bir hata oluştu, bilgileri kontrol edip tekrar deneyiniz",
                });
            });
        } else {
            addEvent(payload).then(() => {
                notification.success({
                    message: "Kayıt işlemi başarıyla yapıldı.",
                });
                resetForm();
                setOpen(false);
                getData();
            }).catch(() => {
                notification.error({
                    message: "Bir hata oluştu, bilgileri kontrol edip tekrar deneyiniz",
                });
            });
        }
    };

    const handleFormChange = (field, value) => {
        setFormFields(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    return (
        <>
            <div className="w-full max-w-[1400px] mx-auto px-4 py-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Etkinlik Listesi</h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setListStatus(listStatus === 1 ? 2 : 1)}
                            className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <span>{listStatus === 1 ? "Arşiv Listesi" : "Aktif Etkinlikler"}</span>
                        </button>
                        <button
                            onClick={() => { resetForm(); setOpen(true); }}
                            className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            <span>Yeni Oluştur</span>
                        </button>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium">Etkinlik Adı</th>
                                    <th className="px-4 py-3 text-left font-medium">Etkinlik Tarihi</th>
                                    <th className="px-4 py-3 text-left font-medium">Personel Sayısı</th>
                                    <th className="px-4 py-3 text-left font-medium">Mekan</th>
                                    <th className="px-4 py-3 text-left font-medium">Durum</th>
                                    <th className="px-4 py-3 text-right font-medium">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {eventList.map((record) => (
                                    <tr key={record.uniqueKey} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-medium text-gray-900 dark:text-white">{record.name}</span>
                                                {record.notes && (
                                                    <span
                                                        className="inline-flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 cursor-pointer"
                                                        title={record.notes}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{moment(record.startDate).format("DD.MM.YYYY")}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                {record.totalPersonnelCount}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{record.placeName}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {record.isClosed ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                    Kayıt Tamamlandı
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                                    Kayıt Devam Ediyor
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => showEditItem(record.uniqueKey)}
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 transition-colors"
                                                    title="Düzenle"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                                    </svg>
                                                    <span>Düzenle</span>
                                                </button>
                                                <LinkRoute
                                                    to={"/event/" + record.uniqueKey}
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-green-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-600 transition-colors"
                                                >
                                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <span>Detay</span>
                                                </LinkRoute>
                                                <ConfirmButton onConfirm={() => onDeleteConfirm(record.uniqueKey)} message="Etkinlik kaydını silmek istediğinizden emin misiniz?">
                                                    <button
                                                        className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600 transition-colors"
                                                    >
                                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                        </svg>
                                                        <span>Sil</span>
                                                    </button>
                                                </ConfirmButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
                    <div className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-white dark:bg-gray-900 shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {editUniqueKey ? "Etkinliği Düzenle" : "Yeni Etkinlik Oluştur"}
                            </h2>
                            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors">
                                Kapat
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="space-y-5">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Etkinlik Adı</label>
                                    <input
                                        type="text"
                                        value={formFields.name}
                                        onChange={(e) => handleFormChange("name", e.target.value)}
                                        placeholder="Etkinlik Adı"
                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                    />
                                    {formErrors.name && <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>}
                                </div>
                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Personel sayısı</label>
                                        <input
                                            type="number"
                                            value={formFields.totalPersonnelCount}
                                            onChange={(e) => handleFormChange("totalPersonnelCount", e.target.value)}
                                            placeholder="Personel Sayısı"
                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        {formErrors.totalPersonnelCount && <p className="mt-1 text-xs text-red-500">{formErrors.totalPersonnelCount}</p>}
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Amir ücreti</label>
                                        <input
                                            type="number"
                                            value={formFields.chiefSalary}
                                            onChange={(e) => handleFormChange("chiefSalary", e.target.value)}
                                            placeholder="Amir Ücreti"
                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        {formErrors.chiefSalary && <p className="mt-1 text-xs text-red-500">{formErrors.chiefSalary}</p>}
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Özel güvenlik ücreti</label>
                                        <input
                                            type="number"
                                            value={formFields.oggSalary}
                                            onChange={(e) => handleFormChange("oggSalary", e.target.value)}
                                            placeholder="Özel Güvenlik Ücreti"
                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        {formErrors.oggSalary && <p className="mt-1 text-xs text-red-500">{formErrors.oggSalary}</p>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Mekan</label>
                                        <select
                                            value={formFields.placeUniqueKey}
                                            onChange={(e) => handleFormChange("placeUniqueKey", e.target.value)}
                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        >
                                            <option value="">Mekan Seçiniz</option>
                                            {eventPlaceList.map((m) => (
                                                <option key={m.uniqueKey} value={m.uniqueKey}>{m.name}</option>
                                            ))}
                                        </select>
                                        {formErrors.placeUniqueKey && <p className="mt-1 text-xs text-red-500">{formErrors.placeUniqueKey}</p>}
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Etkinlik Tarihi</label>
                                        <input
                                            type="date"
                                            value={formFields.startDate}
                                            onChange={(e) => handleFormChange("startDate", e.target.value)}
                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        {formErrors.startDate && <p className="mt-1 text-xs text-red-500">{formErrors.startDate}</p>}
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Notlar</label>
                                    <input
                                        type="text"
                                        value={formFields.notes}
                                        onChange={(e) => handleFormChange("notes", e.target.value)}
                                        placeholder="Etkinlik ile ilgili notlar..."
                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                                    />
                                    {formErrors.notes && <p className="mt-1 text-xs text-red-500">{formErrors.notes}</p>}
                                </div>
                                <div>
                                    <button
                                        onClick={saveForm}
                                        className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-600 transition-colors"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                                        </svg>
                                        <span>{editUniqueKey ? "Güncelle" : "Kaydet"}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
